import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import logo from "../static/media/logo.webp";

const PRIMARY = "#1695e4";
const PRIMARY_DARK = "#0a3d91";
const BLACK = "#10233d";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const parseJsonSafe = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.email.trim() || !form.password.trim()) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/auth/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          password: form.password,
        }),
      });

      const data = await parseJsonSafe(response);

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo iniciar sesión.");
      }

      const finalUser = await login({
        access: data.access,
        refresh: data.refresh,
        user: data.user,
      });

      const currentUser = finalUser || data.user || {};

      const requiresPasswordChange =
        data.requires_password_change === true ||
        data.requiere_cambio_clave === true ||
        data.must_change_password === true ||
        data.change_password_required === true ||
        data.temporary_password === true ||
        currentUser.requires_password_change === true ||
        currentUser.requiere_cambio_clave === true ||
        currentUser.must_change_password === true ||
        currentUser.change_password_required === true ||
        currentUser.temporary_password === true;

      if (requiresPasswordChange) {
        navigate("/actualizar-password", {
          replace: true,
          state: {
            email: currentUser.email || form.email.trim().toLowerCase(),
          },
        });
        return;
      }

      const role = currentUser.role || data.user?.role;

      if (["super_admin", "admin", "staff"].includes(role)) {
        navigate("/admin-panel", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(135deg, #f7faff 0%, #eef4fb 52%, #f8fbff 100%)",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1.08fr) minmax(410px, 0.92fr)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 1.8, md: 6.4 },
            py: { xs: 2.2, md: 6.4 },
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: { xs: 1, md: 3.5 },
              borderRadius: { xs: "22px", md: "38px" },
              background: "linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.66))",
              border: "1px solid rgba(255, 255, 255, 0.78)",
              boxShadow: "0 28px 80px rgba(16, 35, 61, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(14px)",
            },
          }}
        >
          <Stack
            spacing={{ xs: 3, md: 4.1 }}
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 760,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="FactuSysDR"
              sx={{
                width: { xs: "min(100%, 230px)", md: "min(100%, 285px)" },
                height: "auto",
                display: "block",
              }}
            />

            <Stack spacing={2.1} sx={{ maxWidth: 690 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: {
                    xs: "1.78rem",
                    sm: "2.25rem",
                    md: "clamp(2rem, 3.1vw, 3.35rem)",
                  },
                  lineHeight: 1.06,
                  letterSpacing: "-0.04em",
                  color: BLACK,
                  fontWeight: 700,
                }}
              >
                Facturación, inventario y ventas en una plataforma clara para tu negocio.
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "0.92rem", md: "1.01rem" },
                  lineHeight: 1.8,
                  color: "#5d728a",
                  fontWeight: 400,
                  maxWidth: 630,
                }}
              >
                Accede a FactuSysDR para gestionar productos, controlar procesos comerciales y mantener la operación diaria organizada desde un solo panel.
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, minmax(0, 205px))",
                  xl: "repeat(3, minmax(0, 215px))",
                },
                gap: { xs: 1.7, md: 2 },
                maxWidth: { xs: "100%", sm: 690 },
                justifyContent: { xs: "stretch", sm: "start" },
                alignItems: "stretch",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 215 },
                  minHeight: { xs: "auto", sm: 178 },
                  p: 2.35,
                  borderRadius: "24px",
                  background: "rgba(255, 255, 255, 0.74)",
                  border: "1px solid rgba(167, 188, 216, 0.38)",
                  boxShadow: "0 16px 36px rgba(16, 35, 61, 0.06)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                    boxShadow: "0 14px 26px rgba(10, 61, 145, 0.18)",
                    mb: 2,
                  }}
                >
                  <ReceiptLongOutlinedIcon sx={{ fontSize: "1.25rem" }} />
                </Box>

                <Typography sx={{ color: BLACK, fontSize: "1rem", lineHeight: 1.3, fontWeight: 700, mb: 1 }}>
                  Facturación
                </Typography>

                <Typography sx={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.62, fontWeight: 400 }}>
                  Controla documentos, clientes y operaciones comerciales del sistema.
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 215 },
                  minHeight: { xs: "auto", sm: 178 },
                  p: 2.35,
                  borderRadius: "24px",
                  background: "rgba(255, 255, 255, 0.74)",
                  border: "1px solid rgba(167, 188, 216, 0.38)",
                  boxShadow: "0 16px 36px rgba(16, 35, 61, 0.06)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                    boxShadow: "0 14px 26px rgba(10, 61, 145, 0.18)",
                    mb: 2,
                  }}
                >
                  <Inventory2OutlinedIcon sx={{ fontSize: "1.25rem" }} />
                </Box>

                <Typography sx={{ color: BLACK, fontSize: "1rem", lineHeight: 1.3, fontWeight: 700, mb: 1 }}>
                  Inventario
                </Typography>

                <Typography sx={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.62, fontWeight: 400 }}>
                  Administra productos, categorías, marcas y datos necesarios para vender.
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", sm: 215 },
                  minHeight: { xs: "auto", sm: 178 },
                  p: 2.35,
                  borderRadius: "24px",
                  background: "rgba(255, 255, 255, 0.74)",
                  border: "1px solid rgba(167, 188, 216, 0.38)",
                  boxShadow: "0 16px 36px rgba(16, 35, 61, 0.06)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ffffff",
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                    boxShadow: "0 14px 26px rgba(10, 61, 145, 0.18)",
                    mb: 2,
                  }}
                >
                  <PointOfSaleOutlinedIcon sx={{ fontSize: "1.25rem" }} />
                </Box>

                <Typography sx={{ color: BLACK, fontSize: "1rem", lineHeight: 1.3, fontWeight: 700, mb: 1 }}>
                  Ventas
                </Typography>

                <Typography sx={{ color: "#64748b", fontSize: "0.9rem", lineHeight: 1.62, fontWeight: 400 }}>
                  Trabaja con una interfaz preparada para procesos rápidos y ordenados.
                </Typography>
              </Paper>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 1.8, sm: 3.4 },
            py: { xs: 1.4, md: 4.2 },
            background: "linear-gradient(180deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.98))",
            borderLeft: { xs: "none", md: "1px solid rgba(16, 35, 61, 0.06)" },
            borderTop: { xs: "1px solid rgba(16, 35, 61, 0.06)", md: "none" },
          }}
        >
          <Paper
            component={motion.form}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.48, ease: "easeOut" }}
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 440,
              p: { xs: "26px 20px", md: "36px 34px" },
              borderRadius: { xs: "24px", md: "30px" },
              background: "rgba(255, 255, 255, 0.86)",
              border: "1px solid rgba(167, 188, 216, 0.32)",
              boxShadow: "0 30px 72px rgba(16, 35, 61, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.86)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Stack spacing={2.5}>
              <Stack spacing={1.3} alignItems={{ xs: "center", md: "flex-start" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="FactuSysDR"
                  sx={{
                    display: { xs: "block", md: "none" },
                    width: 155,
                    height: "auto",
                    objectFit: "contain",
                    mb: 1,
                  }}
                />

                <Typography
                  component="p"
                  sx={{
                    color: PRIMARY_DARK,
                    fontSize: "0.8rem",
                    lineHeight: 1.3,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Acceso seguro
                </Typography>

                <Typography
                  component="h2"
                  sx={{
                    color: BLACK,
                    fontSize: { xs: "1.72rem", sm: "2.1rem" },
                    lineHeight: 1.1,
                    fontWeight: 700,
                    letterSpacing: "-0.025em",
                  }}
                >
                  Inicio de sesión
                </Typography>

                <Typography
                  sx={{
                    color: "#647688",
                    fontSize: { xs: "0.92rem", md: "1rem" },
                    lineHeight: 1.7,
                    fontWeight: 400,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Ingresa con tus credenciales para acceder al sistema.
                </Typography>
              </Stack>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    color: "#b42318",
                    background: "#fff4f2",
                    border: "1px solid #f3cbc6",
                    borderRadius: "16px",
                    fontSize: "0.93rem",
                    lineHeight: 1.5,
                  }}
                >
                  {error}
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Correo"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 48,
                      background: "rgba(255, 255, 255, 0.86)",
                      borderRadius: "16px",
                      transition: "all 0.25s ease",
                    },
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d2ddeb",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#a9c0df",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused": {
                      background: "#ffffff",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: PRIMARY_DARK,
                      boxShadow: "0 0 0 4px rgba(21, 151, 229, 0.08)",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#64748b",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: PRIMARY_DARK,
                    },
                    "& input:-webkit-autofill": {
                      WebkitTextFillColor: BLACK,
                      WebkitBoxShadow: "0 0 0px 1000px #ffffff inset",
                      transition: "background-color 9999s ease-in-out 0s",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineRoundedIcon sx={{ color: "#8b99ab", fontSize: "1.15rem" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: 48,
                      background: "rgba(255, 255, 255, 0.86)",
                      borderRadius: "16px",
                      transition: "all 0.25s ease",
                    },
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d2ddeb",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#a9c0df",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused": {
                      background: "#ffffff",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: PRIMARY_DARK,
                      boxShadow: "0 0 0 4px rgba(21, 151, 229, 0.08)",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#64748b",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: PRIMARY_DARK,
                    },
                    "& input:-webkit-autofill": {
                      WebkitTextFillColor: BLACK,
                      WebkitBoxShadow: "0 0 0px 1000px #ffffff inset",
                      transition: "background-color 9999s ease-in-out 0s",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#8b99ab", fontSize: "1.15rem" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          disabled={loading}
                          sx={{ color: "#64748b" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 0.5,
                  minHeight: 45,
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                  color: "#ffffff",
                  textTransform: "none",
                  fontSize: "0.96rem",
                  fontWeight: 700,
                  letterSpacing: 0,
                  boxShadow: "0 16px 34px rgba(10, 61, 145, 0.2)",
                  py: 1.2,
                  "&:hover": {
                    background: "linear-gradient(135deg, #1187cf 0%, #083578 100%)",
                    boxShadow: "0 18px 38px rgba(10, 61, 145, 0.24)",
                  },
                  "&:disabled": {
                    color: "#ffffff",
                    background: "linear-gradient(135deg, rgba(22, 149, 228, 0.52), rgba(10, 61, 145, 0.52))",
                  },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "#ffffff" }} /> : "Ingresar"}
              </Button>

              <Box sx={{ mt: 1.2, pt: 2, borderTop: "1px solid rgba(16, 35, 61, 0.08)" }}>
                <Typography sx={{ textAlign: "center", fontSize: 12.5, lineHeight: 1.65, color: "#6a7787" }}>
                  Al ingresar aceptas nuestras{" "}
                  <Link
                    component={RouterLink}
                    to="/politicas"
                    underline="none"
                    sx={{
                      color: PRIMARY_DARK,
                      fontWeight: 700,
                      "&:hover": { color: PRIMARY },
                    }}
                  >
                    políticas de privacidad
                  </Link>{" "}
                  y los{" "}
                  <Link
                    component={RouterLink}
                    to="/terminos-condiciones"
                    underline="none"
                    sx={{
                      color: PRIMARY_DARK,
                      fontWeight: 700,
                      "&:hover": { color: PRIMARY },
                    }}
                  >
                    términos y condiciones
                  </Link>
                  .
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}